import {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
  ReactNode,
} from "react";
import { AxiosError } from "axios";
import { Trans, useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { alertStatusLabel } from "@/alerts/types/machine-alert";
import { ClassificationReviewItem } from "../../types/review";
import {
  computeEventGroups,
  groupIntoEventRows,
  eventGroupColor,
} from "../../utils/event-grouping";
import ReviewCard from "./review-card";
import { Button } from "@/ui/components/ui/button";
import {
  Sparkles,
  ImageOff,
  FilterX,
  AlertTriangle,
  Calendar,
  Loader2,
} from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";

/**
 * ──────────────────────────────────────────────────────────────────
 * ReviewGrid — one component, many honest states.
 *
 * The review page computes a `viewState` and passes it in. The grid
 * renders exactly one of these branches:
 *
 *   onboarding     — No scope yet. Shows the "start an ML export
 *                    review" empty state and nothing else.
 *   loading        — First load (cold cache). Skeleton grid.
 *   empty_scope    — Query returned zero, no refinement. Scope is
 *                    just empty — suggest widening it.
 *   empty_filtered — Query returned zero WITH refinement applied.
 *                    The scope has images, the filters killed them.
 *                    Suggest clearing filters.
 *   error          — Query failed. Retry button.
 *   ready          — Has items. Render the grid. If `isRefetching`,
 *                    overlay a dim layer so the user sees "data is
 *                    updating" without the grid blanking out.
 *
 * The reason this matters: React Query refetches on filter changes.
 * Without `keepPreviousData` + this explicit viewState, the user
 * sees a full-grid flash every time they tweak a filter. With it,
 * the grid stays on screen and dims slightly — huge UX difference.
 * ────────────────────────────────────────────────────────────────── */

export type ReviewGridViewState =
  | "onboarding"
  | "loading"
  | "empty_scope"
  | "empty_filtered"
  | "error"
  | "ready";

interface ReviewGridProps {
  viewState: ReviewGridViewState;
  items: ClassificationReviewItem[];
  isRefetching: boolean;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  error: Error | null;
  scopeImageCount: number;
  /**
   * Applied `alert_status_in`, purely so the filtered-empty state can name the
   * verdict the reviewer asked for — "none pass the current filters" is not a
   * useful answer when the filter is an alert verdict they expected images for.
   */
  alertStatusFilter?: string;
  onLoadMore: () => void;
  onRetry: () => void;
  onClearFilters: () => void;
  selectedItems: Set<number>;
  onToggleSelect: (imageId: number) => void;
  onCardClick: (item: ClassificationReviewItem) => void;
  /** Number of cards per row (client layout preference). */
  cardsPerRow: number;
  /** Whether to draw the dashed "same event" group borders. */
  showGroupHighlight: boolean;
  /** Grid (density) vs one-event-per-row layout. */
  layoutMode: "grid" | "event-rows";
  /** image_id of the keyboard-focused card, or null. */
  focusedImageId?: number | null;
  gridRef?: React.RefObject<HTMLDivElement | null>;
  marqueeRect?: { x: number; y: number; width: number; height: number } | null;
  isMarqueeSelecting?: boolean;
  isMarqueeMode?: boolean;
  onOverlayMouseDown?: (e: React.MouseEvent) => void;
}

/* ──────────────────────────────────────────────────────────────────
 * Backend error parsing
 *
 * The API returns validation errors as:
 * { error: { code: "validation_error", messages: [{ message: "..." }] } }
 *
 * We extract the first human-readable message and detect known error
 * types (e.g. time window too large) so the grid can show a specific
 * helpful empty state instead of a generic "something went wrong".
 * ────────────────────────────────────────────────────────────────── */

interface ParsedApiError {
  message: string;
  isTimeWindowError: boolean;
  maxDays: number | null;
}

interface ApiErrorMessage {
  message: string;
  type?: string;
  field?: string;
  max_days?: number;
}

const parseApiError = (
  error: Error | null,
  t: TFunction<"mlExports">,
): ParsedApiError => {
  const fallback: ParsedApiError = { message: t("review.error.fallback"), isTimeWindowError: false, maxDays: null };
  if (!error) return fallback;

  const axiosErr = error as AxiosError<{
    error?: { code?: string; messages?: ApiErrorMessage[] };
  }>;
  const apiError = axiosErr?.response?.data?.error;

  if (apiError?.messages?.length) {
    const first = apiError.messages[0];
    return {
      message: first.message,
      isTimeWindowError: /time.window.*too.large/i.test(first.message),
      maxDays: first.max_days ?? null,
    };
  }

  return { ...fallback, message: error.message || fallback.message };
};

/* ──────────────────────────────────────────────────────────────────
 * Shared empty-state shell
 * ────────────────────────────────────────────────────────────────── */

interface EmptyStateProps {
  icon: ReactNode;
  title: string;
  description: ReactNode;
  actions?: ReactNode;
}

const EmptyState = ({ icon, title, description, actions }: EmptyStateProps) => (
  <div className="flex flex-col items-center justify-center py-24 px-6 text-center max-w-md mx-auto">
    <div className="mb-4 p-3 rounded-full bg-muted text-muted-foreground">{icon}</div>
    <h3 className="text-lg font-semibold mb-2">{title}</h3>
    <div className="text-sm text-muted-foreground mb-6">{description}</div>
    {actions && <div className="flex items-center gap-2">{actions}</div>}
  </div>
);

/* ──────────────────────────────────────────────────────────────────
 * Skeleton grid (cold first load)
 * ────────────────────────────────────────────────────────────────── */

const gridColumnsStyle = (cardsPerRow: number) => ({
  gridTemplateColumns: `repeat(${cardsPerRow}, minmax(0, 1fr))`,
});

/**
 * Max width (px) for a single card in the event-rows layout. Frames in a row
 * flex to fill the width, but are capped here so a 1-frame event doesn't blow
 * up to a full-width tile — it sits at a sensible size at the row start.
 */
const EVENT_ROW_MAX_CARD_WIDTH = 280;

const SkeletonGrid = ({
  count = 12,
  columns = 6,
}: {
  count?: number;
  columns?: number;
}) => (
  <div className="grid gap-3" style={gridColumnsStyle(columns)}>
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="space-y-2">
        <div className="aspect-square w-full rounded-lg bg-muted animate-pulse" />
        <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
        <div className="h-3 w-1/2 rounded bg-muted animate-pulse" />
      </div>
    ))}
  </div>
);

/* ──────────────────────────────────────────────────────────────────
 * Indeterminate top progress bar (refetch indicator)
 * ────────────────────────────────────────────────────────────────── */

const TopProgressBar = () => (
  <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/10 overflow-hidden pointer-events-none">
    <div
      className="h-full w-1/3 bg-primary"
      style={{
        animation: "reviewgrid-progress 1.2s ease-in-out infinite",
      }}
    />
    <style>{`
      @keyframes reviewgrid-progress {
        0%   { transform: translateX(-100%); }
        50%  { transform: translateX(150%); }
        100% { transform: translateX(400%); }
      }
    `}</style>
  </div>
);

/* ──────────────────────────────────────────────────────────────────
 * Virtualization — only mount the real card when it's near the viewport.
 *
 * With hundreds of loaded images, mounting every ReviewCard (each with a
 * signed-image hook + an <img> to decode) is what makes the grid lag. A
 * single shared IntersectionObserver renders the heavy card only when its
 * cell is near the viewport and swaps in a same-size placeholder otherwise.
 * The placeholder keeps `data-image-id` + its layout slot, so marquee
 * selection, scroll height and group spacing all stay correct.
 * ────────────────────────────────────────────────────────────────── */

/** Register an element for visibility callbacks; returns an unsubscribe fn. */
type ObserveFn = (el: Element, onChange: (visible: boolean) => void) => () => void;

const CardPlaceholder = ({ imageId }: { imageId: number }) => (
  <div
    data-review-card
    data-image-id={imageId}
    className="rounded-lg overflow-hidden bg-card border border-border/60"
  >
    <div className="aspect-square bg-muted animate-pulse" />
    <div className="px-2.5 py-2 space-y-1.5">
      <div className="h-3 w-3/4 rounded bg-muted" />
      <div className="h-2.5 w-1/2 rounded bg-muted" />
    </div>
  </div>
);

interface LazyCardProps {
  item: ClassificationReviewItem;
  isSelected: boolean;
  onToggleSelect: (imageId: number) => void;
  onClick: (item: ClassificationReviewItem) => void;
  groupColor: string | null;
  observe: ObserveFn;
  /** Render the real card immediately (used for the first viewport's worth). */
  eager: boolean;
  /** Keyboard-focused card — gets a focus ring and scrolls into view. */
  isFocused?: boolean;
}

const LazyCard = memo(
  ({ item, isSelected, onToggleSelect, onClick, groupColor, observe, eager, isFocused }: LazyCardProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const [visible, setVisible] = useState(eager);
    // Once measured, lock the cell height so swapping to a placeholder on
    // scroll-out doesn't shift the layout.
    const minHeightRef = useRef<number | undefined>(undefined);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      return observe(el, setVisible);
    }, [observe]);

    useEffect(() => {
      if (visible && ref.current) {
        const h = ref.current.getBoundingClientRect().height;
        if (h) minHeightRef.current = h;
      }
    }, [visible]);

    // Bring the keyboard-focused card into view when it becomes focused.
    useEffect(() => {
      if (isFocused && ref.current) {
        ref.current.scrollIntoView({ block: "nearest", inline: "nearest" });
      }
    }, [isFocused]);

    // Always render the real card when focused so navigation never lands on a
    // placeholder.
    const showReal = visible || isFocused;

    return (
      <div
        ref={ref}
        className={cn(
          "rounded-lg",
          isFocused &&
            "ring-2 ring-sky-400 ring-offset-2 ring-offset-background",
        )}
        style={
          !showReal && minHeightRef.current
            ? { minHeight: minHeightRef.current }
            : undefined
        }
      >
        {showReal ? (
          <ReviewCard
            item={item}
            isSelected={isSelected}
            onToggleSelect={onToggleSelect}
            onClick={onClick}
            groupColor={groupColor}
          />
        ) : (
          <CardPlaceholder imageId={item.image_id} />
        )}
      </div>
    );
  },
);
LazyCard.displayName = "LazyCard";

/* ──────────────────────────────────────────────────────────────────
 * Main
 * ────────────────────────────────────────────────────────────────── */

const ReviewGrid = ({
  viewState,
  items,
  isRefetching,
  isFetchingNextPage,
  hasNextPage,
  error,
  scopeImageCount,
  alertStatusFilter,
  onLoadMore,
  onRetry,
  onClearFilters,
  selectedItems,
  onToggleSelect,
  onCardClick,
  cardsPerRow,
  showGroupHighlight,
  layoutMode,
  focusedImageId,
  gridRef,
  marqueeRect,
  isMarqueeSelecting,
  isMarqueeMode,
  onOverlayMouseDown,
}: ReviewGridProps) => {
  const { t } = useTranslation("mlExports");
  const sentinelRef = useRef<HTMLDivElement>(null);

  // "Resolved alerts" / "Pending or Resolved alerts" / "events with no alert".
  // Only used by the filtered-empty state; null when the filter is unset.
  const alertScopeLabel = useMemo(() => {
    const values = alertStatusFilter?.split(",").filter(Boolean) ?? [];
    if (values.length === 0) return null;
    const verdicts = values.filter((v) => v !== "none");
    const includesNone = values.length !== verdicts.length;
    if (verdicts.length === 0) return t("review.emptyFiltered.noAlertEvents");
    // `alert_status_in` carries raw wire verdicts; display them translated.
    const shown = verdicts.map(alertStatusLabel);
    const joined =
      shown.length === 1
        ? shown[0]
        : t("review.emptyFiltered.verdictJoin", {
            list: shown.slice(0, -1).join(", "),
            last: shown[shown.length - 1],
          });
    return t(
      includesNone
        ? "review.emptyFiltered.verdictAlertsOrNone"
        : "review.emptyFiltered.verdictAlerts",
      { verdicts: joined },
    );
  }, [alertStatusFilter, t]);

  // Per-item event-group info — colours runs of consecutive same-event cards.
  // Skipped entirely when the highlight toggle is off.
  const eventGroups = useMemo(
    () => (showGroupHighlight ? computeEventGroups(items) : []),
    [items, showGroupHighlight],
  );

  // One row per consecutive event run (event-rows layout only).
  const eventRows = useMemo(
    () => (layoutMode === "event-rows" ? groupIntoEventRows(items) : []),
    [items, layoutMode],
  );

  // Border colour per row, indexed by multi-frame-group position so adjacent
  // events get clearly distinct colours (single-frame rows get none).
  const eventRowColors = useMemo(() => {
    if (layoutMode !== "event-rows" || !showGroupHighlight)
      return [] as (string | null)[];
    let groupIndex = 0;
    return eventRows.map((row) =>
      row.items.length >= 2 ? eventGroupColor(groupIndex++) : null,
    );
  }, [eventRows, layoutMode, showGroupHighlight]);

  // Render the first dozen cards eagerly so the initial viewport has no
  // placeholder flash; the rest hydrate on scroll. Used by both layouts.
  const eagerIds = useMemo(
    () => new Set(items.slice(0, 12).map((i) => i.image_id)),
    [items],
  );

  // ─── Shared visibility observer for card virtualization ──────────
  // One IntersectionObserver for the whole grid; each LazyCard registers its
  // element and gets a visible/hidden callback. rootMargin renders cards a bit
  // before they scroll into view to avoid blank flashes.
  const visibilityCallbacks = useRef(
    new Map<Element, (visible: boolean) => void>(),
  );
  const cardObserver = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibilityCallbacks.current.get(entry.target)?.(entry.isIntersecting);
        }
      },
      { rootMargin: "600px 0px" },
    );
    cardObserver.current = observer;
    // Child effects run before parent effects, so some cards may already be
    // registered — start observing them now.
    for (const el of visibilityCallbacks.current.keys()) observer.observe(el);
    return () => {
      observer.disconnect();
      cardObserver.current = null;
    };
  }, []);

  const observe = useCallback<ObserveFn>((el, onChange) => {
    visibilityCallbacks.current.set(el, onChange);
    cardObserver.current?.observe(el);
    return () => {
      visibilityCallbacks.current.delete(el);
      cardObserver.current?.unobserve(el);
    };
  }, []);

  // Prefetch next page when the sentinel enters the viewport (400px early).
  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        onLoadMore();
      }
    },
    [hasNextPage, isFetchingNextPage, onLoadMore]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || viewState !== "ready") return;

    const observer = new IntersectionObserver(handleIntersect, {
      rootMargin: "400px",
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersect, viewState]);

  // ─── Empty / loading branches ────────────────────────────────────
  if (viewState === "onboarding") {
    return (
      <EmptyState
        icon={<Sparkles className="h-6 w-6" />}
        title={t("review.onboarding.title")}
        description={t("review.onboarding.description")}
        actions={
          <p className="text-xs text-muted-foreground">
            <Trans
              t={t}
              i18nKey="review.onboarding.hint"
              components={{ scope: <span className="font-semibold" /> }}
            />
          </p>
        }
      />
    );
  }

  if (viewState === "loading") {
    return <SkeletonGrid count={12} columns={cardsPerRow} />;
  }

  if (viewState === "error") {
    const parsed = parseApiError(error, t);

    if (parsed.isTimeWindowError) {
      return (
        <EmptyState
          icon={<Calendar className="h-6 w-6" />}
          title={t("review.error.timeWindowTitle")}
          description={
            <Trans
              t={t}
              i18nKey="review.error.timeWindowDescription"
              values={{ days: formatNumber(parsed.maxDays ?? 31) }}
              components={{ days: <span className="font-semibold" /> }}
            />
          }
        />
      );
    }

    return (
      <EmptyState
        icon={<AlertTriangle className="h-6 w-6 text-destructive" />}
        title={t("review.error.title")}
        description={parsed.message}
        actions={
          <Button size="sm" onClick={onRetry}>
            {t("review.error.retry")}
          </Button>
        }
      />
    );
  }

  if (viewState === "empty_scope") {
    return (
      <EmptyState
        icon={<ImageOff className="h-6 w-6" />}
        title={t("review.emptyScope.title")}
        description={t("review.emptyScope.description")}
      />
    );
  }

  if (viewState === "empty_filtered") {
    return (
      <EmptyState
        icon={<FilterX className="h-6 w-6" />}
        title={
          alertScopeLabel
            ? t("review.emptyFiltered.titleWithAlert", { scope: alertScopeLabel })
            : t("review.emptyFiltered.title")
        }
        description={
          <Trans
            t={t}
            i18nKey="review.emptyFiltered.description"
            values={{ total: formatNumber(scopeImageCount) }}
            components={{ total: <span className="font-semibold" /> }}
          />
        }
        actions={
          <Button size="sm" onClick={onClearFilters}>
            {t("review.emptyFiltered.clearFilters")}
          </Button>
        }
      />
    );
  }

  // ─── Ready — render the actual grid ──────────────────────────────
  return (
    <div className="relative" ref={gridRef}>
      {isRefetching && <TopProgressBar />}

      {/* Transparent overlay — captures mouse events for marquee drag */}
      {isMarqueeMode && (
        <div
          className="absolute inset-0 z-20 cursor-crosshair bg-primary/5 border-2 border-dashed border-primary/20 rounded-lg"
          onMouseDown={onOverlayMouseDown}
        />
      )}

      {/* Marquee selection rectangle */}
      {isMarqueeSelecting && marqueeRect && (
        <div
          className="absolute border-2 border-primary/60 bg-primary/10 rounded-sm pointer-events-none z-30"
          style={{
            left: marqueeRect.x,
            top: marqueeRect.y,
            width: marqueeRect.width,
            height: marqueeRect.height,
          }}
        />
      )}

      <div
        className={cn(
          "transition-opacity duration-150",
          // Dim the whole grid while a refetch is in flight. The old
          // cards stay visible and interactive-ish; the progress bar
          // above says "new data is coming".
          isRefetching && "opacity-60"
        )}
      >
        {layoutMode === "event-rows" ? (
          <div className="space-y-3">
            {eventRows.map((row, rowIdx) => {
              // Multi-frame events get a dashed border; colour comes from the
              // per-row palette so adjacent events stay visually distinct.
              const rowColor = eventRowColors[rowIdx] ?? null;
              return (
                <div key={`${row.uuid}-${rowIdx}`} className="flex gap-3">
                  {row.items.map((item) => (
                    <div
                      key={item.image_id}
                      className="flex-1 min-w-0"
                      style={{ maxWidth: EVENT_ROW_MAX_CARD_WIDTH }}
                    >
                      <LazyCard
                        item={item}
                        isSelected={selectedItems.has(item.image_id)}
                        onToggleSelect={onToggleSelect}
                        onClick={onCardClick}
                        groupColor={rowColor}
                        observe={observe}
                        eager={eagerIds.has(item.image_id)}
                        isFocused={item.image_id === focusedImageId}
                      />
                    </div>
                  ))}
                </div>
              );
            })}

            {isFetchingNextPage && (
              <div className="flex gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={`next-${i}`}
                    className="flex-1 min-w-0 space-y-2"
                    style={{ maxWidth: EVENT_ROW_MAX_CARD_WIDTH }}
                  >
                    <div className="aspect-square w-full rounded-lg bg-muted animate-pulse" />
                    <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="grid gap-3" style={gridColumnsStyle(cardsPerRow)}>
            {items.map((item, index) => (
              <LazyCard
                key={item.image_id}
                item={item}
                isSelected={selectedItems.has(item.image_id)}
                onToggleSelect={onToggleSelect}
                onClick={onCardClick}
                groupColor={eventGroups[index]?.color ?? null}
                observe={observe}
                eager={eagerIds.has(item.image_id)}
                isFocused={item.image_id === focusedImageId}
              />
            ))}

            {isFetchingNextPage &&
              Array.from({ length: 4 }).map((_, i) => (
                <div key={`next-${i}`} className="space-y-2">
                  <div className="aspect-square w-full rounded-lg bg-muted animate-pulse" />
                  <div className="h-3 w-3/4 rounded bg-muted animate-pulse" />
                </div>
              ))}
          </div>
        )}

        {/* Sentinel for infinite scroll prefetch. */}
        <div ref={sentinelRef} className="h-1" />

        {/* Fallback spinner (shouldn't normally be visible — inline
            skeletons above cover the common case). */}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewGrid;
